// 道具「效果行」的唯一前端口径
// ------------------------------------------------------------------
// 设计原则（2026-10-28）：**数值只在配置里存一份**，描述文案（itemDesc）只写风味，不写任何数字。
// 否则每次调数值都要手工同步描述，必然脱节（历史上 9 道料理的描述全部过期）。
//
// 数值来源，与后端 useItem 的两个 Bean 严格对齐，别只看某一个字段：
//   1) 体力恢复（scope='detail' && property='strength'）：
//      - 食物（beanName=food，走 FoodBean）→ 取 itemtype.property_val；
//        食物实例的 fantasy_item.strength 恒为 -1，取它会永远算不出来。
//      - 药片（大力丸等，走 PillBean）→ 取实例 strength；它的 property_val 配的是 0。
//      - 大力丸存在 2147483647 这样的"补满"实例值，统一按 STAMINA_FULL_THRESHOLD 视为补满。
//   2) 限时增益（itemtype.buff_effect，形如 "meal_produce:100,meal_rare:10"）：
//      数值是**千分比**（100 = 10%），不是百分比，别直接当 % 显示。
//   3) 工具（鱼竿/矿镐）：数值不在 itemtype 的属性列里，由后端 ToolSpecRegistry 持有
//      （bean 模块唯一真值），后端把「规格 + 类型耐久上限」拼成整串后通过 item.toolEffect 下发，
//      前端只拆行、不抄数值（见 toolEffectLinesOf）。

/** 视作"直接补满"的阈值（后端 addUserStrength 会按 strengthtop 截断，前端只做展示） */
export const STAMINA_FULL_THRESHOLD = 999999;

/** buff key → 展示名（与 fantasy_itemtype.buff_effect 的 key 对应，未知 key 原样显示） */
export const BUFF_LABELS = {
  meal_produce: '劳作双倍',
  meal_mastery: '熟练度双倍',
  meal_rare: '稀有收获'
};

/**
 * 估算该道具使用后可增加的体力值（0 表示不是体力类道具 / 算不出来）
 * @param {Object} item 需含 scope、property、propertyVal、strength、beanName（商店道具可无 beanName/strength）
 * @returns {number}
 */
export function staminaGainOf(item) {
  if (!item || item.scope !== 'detail' || item.property !== 'strength') {
    return 0;
  }
  const propVal = parseInt(item.propertyVal, 10);
  const instVal = parseInt(item.strength, 10);
  const isFood = (item.beanName || '').toLowerCase() === 'food';
  if (isFood) {
    return !isNaN(propVal) && propVal > 0 ? propVal : 0;
  }
  if (!isNaN(instVal) && instVal > 0) {
    return instVal;
  }
  return !isNaN(propVal) && propVal > 0 ? propVal : 0;
}

/**
 * 解析 buff_effect 配置为可展示的效果项
 * @param {Object} item 需含 buffEffect（形如 "meal_produce:100,meal_rare:10"，值为千分比）
 * @returns {Array<{key: string, permille: number, percentText: string, label: string}>}
 */
export function buffEffectsOf(item) {
  const raw = item && item.buffEffect;
  if (!raw) {
    return [];
  }
  const out = [];
  String(raw).split(',').forEach(function (pair) {
    const kv = pair.split(':');
    if (kv.length !== 2) {
      return;
    }
    const key = (kv[0] || '').trim();
    const permille = parseInt((kv[1] || '').trim(), 10);
    if (!key || isNaN(permille) || permille <= 0) {
      return;
    }
    out.push({
      key: key,
      permille: permille,
      percentText: permilleToPercent(permille),
      label: BUFF_LABELS[key] || key
    });
  });
  return out;
}

/** 千分比 → 百分比文案：100 → "10%"、5 → "0.5%" */
function permilleToPercent(permille) {
  return parseFloat((permille / 10).toFixed(2)) + '%';
}

/**
 * 生成"可恢复体力"说明，带上当前体力换算（无 detail 时降级为纯数值）
 * @param {Object} item 道具
 * @param {Object} [detail] 玩家详情（含 strength / strengthtop）
 * @returns {{text: string, full: boolean}|null} 非体力道具或已变质返回 null
 */
export function staminaMetaOf(item, detail) {
  const gain = staminaGainOf(item);
  // 变质的不能吃，别再显示"可恢复"误导玩家（处置提示由保鲜逻辑单独给）
  if (!gain || !item || item.state === 'spoiled') {
    return null;
  }
  const toFull = gain >= STAMINA_FULL_THRESHOLD;
  const cur = parseInt((detail || {}).strength, 10);
  const top = parseInt((detail || {}).strengthtop, 10);
  if (isNaN(cur) || isNaN(top) || top <= 0) {
    return { text: toFull ? '服用后体力补满' : '可恢复体力 +' + gain, full: false };
  }
  if (cur >= top) {
    return { text: '当前体力已满（' + cur + '/' + top + '），无需服用', full: true };
  }
  if (toFull) {
    return { text: '服用后体力补满：' + cur + ' → ' + top, full: false };
  }
  const real = Math.min(gain, top - cur);
  if (real < gain) {
    return { text: '可恢复体力 +' + real + '（' + cur + ' → ' + top + '，超出上限部分无效）', full: false };
  }
  return { text: '可恢复体力 +' + gain + '（体力 ' + cur + ' → ' + (cur + gain) + '）', full: false };
}

/**
 * 工具（鱼竿/矿镐）效果行。
 * <p>
 * 与前两类不同：工具的数值不在 fantasy_itemtype 的 property_val / buff_effect 里，
 * 而是由后端 {@code com.huanzhidadi.bean.spec.ToolSpecRegistry} 统一持有，
 * 连同类型耐久上限一起经 {@code ToolEffectDescriber} 拼好，通过 item.toolEffect 下发。
 * 前端只负责按「；」拆行 —— **不要在这里再抄一份数值**，否则又会变成两处真值。
 *
 * @param {Object} item 需含 toolEffect（形如 "收线速度 ×1.3；稀有鱼上钩率 +25‰；…；耐久上限 30"）
 * @returns {string[]}
 */
export function toolEffectLinesOf(item) {
  const raw = item && item.toolEffect;
  if (!raw) {
    return [];
  }
  return String(raw)
    .split('；')
    .map(function (s) { return s.trim(); })
    .filter(function (s) { return s !== ''; });
}

/**
 * 工具卡片小字：当前耐久 / 类型耐久上限（上限来自 itemtype.durability，不是实例耐久）
 * @param {Object} item 需含 maxDurability，可选 durability（实例剩余耐久）
 * @returns {string} 例如 "耐久 26/30"，非工具或上限未知返回空串
 */
export function toolShortText(item) {
  if (!item || !item.toolEffect) {
    return '';
  }
  const max = parseInt(item.maxDurability, 10);
  if (isNaN(max) || max <= 0) {
    return '';
  }
  const cur = parseInt(item.durability, 10);
  return '耐久 ' + (!isNaN(cur) && cur >= 0 ? cur : '?') + '/' + max;
}

/**
 * 卡片小字用的短效果文案（不依赖玩家状态）
 * @param {Object} item 道具
 * @returns {string} 例如 "体力+30" / "体力+满" / "稀有收获+1%"，无效果返回空串
 */
export function staminaShortText(item) {
  const gain = staminaGainOf(item);
  if (!gain || (item && item.state === 'spoiled')) {
    return '';
  }
  return '体力+' + (gain >= STAMINA_FULL_THRESHOLD ? '满' : gain);
}

/**
 * 卡片小字：体力 + 全部限时增益，用 " · " 连接（变质道具一律不显示）
 * @param {Object} item 道具
 * @returns {string} 例如 "体力+150 · 稀有收获+1%"
 */
export function effectShortText(item) {
  if (!item || item.state === 'spoiled') {
    return '';
  }
  // 工具（鱼竿/矿镐）：效果行来自后端 toolEffect，小字只给「耐久 当前/上限」
  if (item.toolEffect) {
    return toolShortText(item);
  }
  const parts = [];
  const stamina = staminaShortText(item);
  if (stamina) {
    parts.push(stamina);
  }
  buffEffectsOf(item).forEach(function (b) {
    parts.push(b.label + '+' + b.percentText);
  });
  return parts.join(' · ');
}

/**
 * 卡片小字（窄卡片专用）：体力给数值，增益只给个数，避免撑爆图标下方的单行文字。
 * 具体增益项点开卡片图片查看（effectLinesOf）。
 * @param {Object} item 道具
 * @returns {string} 例如 "体力+150" / "体力+180 · 增益×3"
 */
export function effectCardText(item) {
  if (!item || item.state === 'spoiled') {
    return '';
  }
  // 工具（鱼竿/矿镐）：窄卡片只显示耐久，效果明细点开看
  if (item.toolEffect) {
    return toolShortText(item);
  }
  const parts = [];
  const stamina = staminaShortText(item);
  if (stamina) {
    parts.push(stamina);
  }
  const buffs = buffEffectsOf(item);
  if (buffs.length === 1) {
    parts.push(buffs[0].label + '+' + buffs[0].percentText);
  } else if (buffs.length > 1) {
    parts.push('增益×' + buffs.length);
  }
  return parts.join(' · ');
}

/**
 * 点击/确认框用的完整效果行（每项一行）
 * @param {Object} item 道具
 * @param {Object} [detail] 玩家详情（含 strength / strengthtop）
 * @returns {string[]} 例如 ["可恢复体力 +30（体力 120 → 150）", "劳作双倍 +10%"]
 */
export function effectLinesOf(item, detail) {
  if (!item || item.state === 'spoiled') {
    return [];
  }
  // 工具（鱼竿/矿镐）：后端已按 ToolSpecRegistry 拼好整串效果，拆行即用
  if (item.toolEffect) {
    return toolEffectLinesOf(item);
  }
  const lines = [];
  const meta = staminaMetaOf(item, detail);
  if (meta) {
    lines.push(meta.text);
  }
  buffEffectsOf(item).forEach(function (b) {
    lines.push(b.label + ' +' + b.percentText + '（限时增益）');
  });
  return lines;
}

/** 一天毫秒数（保鲜倒计时换算用） */
export const DAY_MS = 86400000;

/**
 * 保鲜倒计时文案：「剩3小时 / 剩2天」，已过期返回空串。
 * <p>
 * 卡片角标、桌面 hover 文案、移动端点击提示三处共用这一个函数 ——
 * 之前角标自己算一遍、提示里只有绝对到期时间，玩家点开看到的和角标对不上。
 *
 * @param {number} expireAt 到期时刻（毫秒时间戳）
 * @param {number} [now] 当前时刻，便于测试注入
 * @returns {string} 空串表示已过期或无到期时间
 */
export function freshRemainText(expireAt, now) {
  const at = parseInt(expireAt, 10);
  if (isNaN(at) || at <= 0) {
    return '';
  }
  const remain = at - (now || Date.now());
  if (remain <= 0) {
    return '';
  }
  if (remain >= DAY_MS) {
    return '剩' + Math.ceil(remain / DAY_MS) + '天';
  }
  return '剩' + Math.max(1, Math.floor(remain / 3600000)) + '小时';
}
